import {PUNISHMENT_KEYS, usePunishmentTemplateQuery} from "@/api/punishmentApi";
import Button from "@/components/Common/Button";
import Loader from "@/components/Common/kpi/Loader";
import DeleteTemplateModal from "@/components/Common/modal/DeleteTemplateModal";
import type {PunishmentTemplateTranslationKeySerializer} from "@/generated/Punishment";
import {useDiscipline} from "@/hooks/useDiscipline";
import {client} from "@/httpClient";
import {OTHER_TEMPLATE_ID} from "@/store/constants";
import {
  disciplinaryModalAtom,
  disciplineTabAtom,
  disciplineTemplateFormAtom,
  isDeleteTemplateModalOpenAtom,
} from "@/store/jotaiAtoms";
import {getRemainingTime, isDisciplineActive} from "@/utils/disciplineUtils";
import {useMutation} from "@tanstack/react-query";
import type {AxiosError, AxiosResponse} from "axios";
import {useAtom, useAtomValue} from "jotai";
import {isEmpty} from "lodash";
import {memo, useCallback, type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";

const ModalButtonRow: FC<{hideTemplateButton?: boolean; onBack: () => void; onSuccessAction: () => void}> = ({
  hideTemplateButton,
  onBack,
  onSuccessAction,
}) => {
  const {disciplineTabInPunishmentType, getLabelByDisciplineType, invalidateQueries, isStrongerDisciplineActive} =
    useDiscipline();
  const {t} = useTranslation("influencer_discipline");
  const disciplineTab = useAtomValue(disciplineTabAtom);
  const templatesQuery = usePunishmentTemplateQuery(disciplineTabInPunishmentType);
  const [template, setTemplate] = useAtom(disciplineTemplateFormAtom);
  const disciplinaryData = useAtomValue(disciplinaryModalAtom);
  const {selectedDiscipline} = disciplinaryData;
  const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useAtom(isDeleteTemplateModalOpenAtom);

  const onSuccessDisciplineAction = useCallback(() => {
    invalidateQueries();
    onSuccessAction();
  }, [onSuccessAction, invalidateQueries]);

  const {mutateAsync: saveTemplate, isPending: isSavingTemplate} = useMutation<
    AxiosResponse<PunishmentTemplateTranslationKeySerializer>,
    AxiosError<{error: string}>
  >({
    mutationKey: PUNISHMENT_KEYS.mPunishmentTemplates,
    mutationFn: () =>
      client.post(PUNISHMENT_KEYS.mPunishmentTemplates[0], {
        body_de: template.translations.find(t => t.language_code === "de_CH")?.body,
        body_en: template.translations.find(t => t.language_code === "en_EN")?.body,
        key: template.name,
        punishment_type: disciplineTabInPunishmentType,
        title_de: template.translations.find(t => t.language_code === "de_CH")?.title,
        title_en: template.translations.find(t => t.language_code === "en_EN")?.title,
      }),
    onSuccess: async response => {
      await templatesQuery.refetch();
      setTemplate(response.data as typeof template);
    },
    onError: (error: any) => {
      toast.error(error.response?.data.detail);
    },
  });

  const {mutate: endDiscipline, isPending: isEndPending} = useMutation({
    mutationKey: [PUNISHMENT_KEYS.mUpdatePunishment(disciplinaryData.selectedDiscipline?.id!), "end_discipline"],
    mutationFn: () =>
      client.patch(PUNISHMENT_KEYS.mUpdatePunishment(disciplinaryData.selectedDiscipline?.id!)[0], {
        end: true,
      }),
    onSuccess: onSuccessDisciplineAction,
  });

  const {mutate: excuseDiscipline, isPending: isExcusePending} = useMutation({
    mutationKey: [PUNISHMENT_KEYS.mUpdatePunishment(disciplinaryData.selectedDiscipline?.id!), "excuse_discipline"],
    mutationFn: () =>
      client.patch(PUNISHMENT_KEYS.mUpdatePunishment(disciplinaryData.selectedDiscipline?.id!)[0], {
        excuse: true,
      }),
    onSuccess: onSuccessDisciplineAction,
  });

  const {mutate: initiateDiscipline, isPending: isInitiatePending} = useMutation({
    mutationKey: PUNISHMENT_KEYS.mPunishment,
    mutationFn: () =>
      client.post(PUNISHMENT_KEYS.mPunishment[0], {
        body_de: template.translations.find(t => t.language_code === "de_CH")?.body,
        body_en: template.translations.find(t => t.language_code === "en_EN")?.body,
        campaign_application_id: disciplinaryData?.campaignApplication?.id,
        punishment_days: disciplinaryData?.disciplineDuration,
        punishment_type: disciplineTabInPunishmentType,
        re_verification_allowed: disciplinaryData?.isTimeToggleEnabled,
        template_id: template.id === OTHER_TEMPLATE_ID ? undefined : template.id,
        title_de: template.translations.find(t => t.language_code === "de_CH")?.title,
        title_en: template.translations.find(t => t.language_code === "en_EN")?.title,
        user_id: disciplinaryData?.userId,
      }),
    onSuccess: onSuccessDisciplineAction,
  });

  const {mutate: updatePunishment, isPending: isUpdatePending} = useMutation({
    mutationKey: [PUNISHMENT_KEYS.mUpdatePunishment(disciplinaryData.selectedDiscipline?.id!), "update_discipline"],
    mutationFn: () =>
      client.patch(PUNISHMENT_KEYS.mUpdatePunishment(disciplinaryData.selectedDiscipline?.id!)[0], {
        body_de: template.translations.find(t => t.language_code === "de_CH")?.body,
        body_en: template.translations.find(t => t.language_code === "en_EN")?.body,
        days_left:
          getRemainingTime(selectedDiscipline?.expiration_date).remainingDays === disciplinaryData?.disciplineDuration
            ? undefined
            : disciplinaryData?.disciplineDuration,
        re_verification_allowed: disciplinaryData?.isTimeToggleEnabled,
        title_de: template.translations.find(t => t.language_code === "de_CH")?.title,
        title_en: template.translations.find(t => t.language_code === "en_EN")?.title,
      }),
    onSuccess: onSuccessDisciplineAction,
  });

  const {mutateAsync: deleteTemplate, isPending: isTemplateDeleting} = useMutation({
    mutationKey: [PUNISHMENT_KEYS.mPunishmentTemplates, "delete_template"],
    mutationFn: (templateId: number) =>
      client.delete(PUNISHMENT_KEYS.mPunishmentTemplates[0], {data: {template_id: templateId}}),
    onSuccess: async () => {
      await templatesQuery.refetch();
    },
  });

  const isTemplateFieldMissing = useCallback(
    () =>
      isEmpty(template.translations[0].body) ||
      isEmpty(template.translations[0].title) ||
      isEmpty(template.translations[1].body) ||
      isEmpty(template.translations[1].title),
    [template.translations],
  );

  return (
    <>
      {showDeleteTemplateModal && (
        <DeleteTemplateModal
          onDelete={async () => {
            await deleteTemplate(template.id!);
            setTemplate(templatesQuery.data?.[0]!);
          }}
        />
      )}
      <div className="border-neutral-white-dark grid shrink-0 grow grid-cols-2 gap-3 border-t-1 p-5 md:flex">
        <Button buttonStyle="White" className="flex-1" onClick={onBack}>
          {t("back")}
        </Button>
        {!selectedDiscipline && template.id !== OTHER_TEMPLATE_ID && (
          <Button
            isDisabled={isTemplateDeleting}
            onClick={() => setShowDeleteTemplateModal(true)}
            buttonStyle="Blue"
            className="h-14 px-8 py-2">
            {isTemplateDeleting ? <Loader /> : t("delete_template")}
          </Button>
        )}
        {!selectedDiscipline &&
          template.id === OTHER_TEMPLATE_ID &&
          !hideTemplateButton &&
          !isStrongerDisciplineActive && (
            <Button
              isDisabled={isSavingTemplate}
              onClick={async () => {
                await saveTemplate();
              }}
              buttonStyle="Blue"
              className="h-14 px-8 py-2">
              {isSavingTemplate ? <Loader /> : t("save_template")}
            </Button>
          )}
        {selectedDiscipline ? (
          <>
            <Button
              buttonStyle="Green"
              className="flex-1"
              isLoading={isExcusePending}
              onClick={() => excuseDiscipline()}>
              <span className="z-10 font-medium md:text-lg">{`${t("excuse")} ${disciplineTab !== "UNVERIFY" ? getLabelByDisciplineType(disciplineTab) : ""}`}</span>
            </Button>
            {isDisciplineActive(selectedDiscipline) && (
              <Button
                buttonStyle="Gradient"
                className="flex-1"
                isLoading={isEndPending}
                onClick={() => endDiscipline()}>
                <span className="z-10 font-medium md:text-lg">
                  {disciplineTab === "UNVERIFY"
                    ? t("reverify")
                    : t("end", {punishment: getLabelByDisciplineType(disciplineTab)})}
                </span>
              </Button>
            )}
            <Button
              buttonStyle="Blue"
              className="flex-1"
              isLoading={isUpdatePending}
              isDisabled={
                (disciplineTab === "UNVERIFY" && !disciplinaryData.selectedDiscipline) || isTemplateFieldMissing()
              }
              onClick={() => updatePunishment()}>
              <span className="z-10 font-medium md:text-lg">{t("save_changes")}</span>
            </Button>
          </>
        ) : (
          <Button
            buttonStyle="Gradient"
            className="flex-1"
            isLoading={isInitiatePending}
            isDisabled={
              (disciplineTab === "UNVERIFY" && !disciplinaryData.selectedUnverificationType) ||
              isTemplateFieldMissing() ||
              (disciplineTab !== "PERMANENT_BAN" &&
                disciplinaryData.isTimeToggleEnabled &&
                !disciplinaryData.disciplineDuration &&
                disciplinaryData.selectedUnverificationType !== "ugc")
            }
            onClick={() => initiateDiscipline()}>
            <span className="z-10 font-medium md:text-lg">
              {disciplineTab === "UNVERIFY"
                ? t("unverify", {
                    platform: t(`common:${disciplinaryData.selectedUnverificationType ?? ""}`),
                  })
                : t("initiate", {punishment: getLabelByDisciplineType(disciplineTab)})}
            </span>
          </Button>
        )}
      </div>
    </>
  );
};

export default memo(ModalButtonRow);
